import React, { useState } from "react";
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import RecordVoiceOverIcon from "@mui/icons-material/RecordVoiceOver";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { TranscriptParagraph } from "../../../shared/interfaces/Transcript";
import { EditableSentence } from "./EditableSentence";
import { EntrySpeakerSelect } from "./EntrySpeakerSelect";
import { EntryActions } from "./EntryActions";
import {
  formatSpeakerLabel,
  getSpeakerColorByLabel,
} from "./speakerColors";
import { formatTimestamp } from "../utils/timeFormat";
import { isLowSpeakerConfidence } from "../utils/confidence";

interface TranscriptEntryEditorProps {
  entry: TranscriptParagraph;
  speakers: string[];
  isActive: boolean;
  /** Original sentence texts (for dirty highlighting). */
  originalSentences?: string[];
  disabled?: boolean;
  onSeek: (t: number) => void;
  onReassignSpeaker: (label: string) => void;
  /** Confirm the AI's current speaker label for a sentence (clears the low-confidence flag). */
  onVerifySpeaker: (sentenceIdx: number) => void;
  onSentenceChange: (sentenceIdx: number, text: string) => void;
  /** Split a single sentence out into a new entry for another speaker. */
  onSplitSentence: (sentenceIdx: number, speakerLabel: string) => void;
  /** Delete a single sentence from this entry. */
  onDeleteSentence: (sentenceIdx: number) => void;
  onRequestDelete: () => void;
  /** Slot for the validation banner (word-gate / empty). */
  statusBanner?: React.ReactNode;
  /** Emphasis when this entry needs attention. */
  attention?: "warning" | "error";
}

export const TranscriptEntryEditor: React.FC<TranscriptEntryEditorProps> = ({
  entry,
  speakers,
  isActive,
  originalSentences,
  disabled = false,
  onSeek,
  onReassignSpeaker,
  onVerifySpeaker,
  onSentenceChange,
  onSplitSentence,
  onDeleteSentence,
  onRequestDelete,
  statusBanner,
  attention,
}) => {
  const theme = useTheme();
  const accent =
    attention === "error"
      ? theme.palette.error.main
      : attention === "warning"
        ? theme.palette.warning.main
        : getSpeakerColorByLabel(entry.speaker, speakers, theme.palette.mode)
            .primary;

  const [splitMenu, setSplitMenu] = useState<{
    anchor: HTMLElement;
    sentenceIdx: number;
  } | null>(null);

  // Speakers a sentence can be moved to (anyone but this entry's own speaker).
  const otherSpeakers = speakers.filter((s) => s !== String(entry.speaker));
  // Splitting only makes sense with >1 sentence; a lone sentence uses the
  // entry-level speaker dropdown instead.
  const canSplit = !disabled && entry.sentences.length > 1 && otherSpeakers.length > 0;
  // Deleting a sentence needs >1 sentence; a lone sentence is removed via the
  // entry-level delete (which drops the whole entry).
  const canDeleteSentence = !disabled && entry.sentences.length > 1;
  const showActions = canSplit || canDeleteSentence;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        border: "1px solid",
        borderColor: isActive ? "primary.main" : "divider",
        borderLeft: `3px solid ${accent}`,
        borderRadius: 2,
        backgroundColor: isActive
          ? alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.16 : 0.06)
          : alpha(theme.palette.background.paper, theme.palette.mode === "dark" ? 0.6 : 1),
        transition: "background-color 0.2s ease, border-color 0.2s ease",
      }}
    >
      <Box display="flex" alignItems="center" gap={1} mb={1}>
        <EntrySpeakerSelect
          value={String(entry.speaker ?? "")}
          speakers={speakers}
          onChange={onReassignSpeaker}
          disabled={disabled}
        />
        <Typography
          variant="caption"
          sx={{ color: "text.secondary", fontFamily: "monospace", fontSize: "0.7rem" }}
        >
          {formatTimestamp(entry.start)} – {formatTimestamp(entry.end)}
        </Typography>
        <Box flexGrow={1} />
        <EntryActions
          onJump={() => onSeek(entry.start)}
          onDelete={onRequestDelete}
          disabled={disabled}
        />
      </Box>

      {statusBanner}

      <Typography
        component="div"
        variant="body1"
        sx={{ lineHeight: 1.8, color: "text.primary" }}
      >
        {entry.sentences.map((sentence, sIdx) => {
          const isLowConfidence = isLowSpeakerConfidence(sentence);
          // Reveal the hover actions when the entry has split/delete affordances,
          // or when this sentence's speaker label still needs verifying.
          const revealActions = showActions || (isLowConfidence && !disabled);
          return (
          // Key by the sentence's own timestamps, not the array index: index keys
          // let React reuse an EditableSentence (with its local draft/isEditing
          // state) for a different sentence after a delete/split, stranding an
          // in-progress edit on the wrong row. start/end survive text edits and
          // stay unique within an entry.
          <Box
            key={`${sentence.start}-${sentence.end}`}
            sx={{
              position: "relative",
              display: "block",
              // Reveal on keyboard focus too, not just hover, so keyboard
              // users can see the action they've tabbed onto.
              "&:hover .sentence-action, &:focus-within .sentence-action": {
                opacity: revealActions ? 1 : 0,
              },
            }}
          >
            <EditableSentence
              text={sentence.text}
              isDirty={
                originalSentences
                  ? originalSentences[sIdx] !== sentence.text
                  : false
              }
              isLowConfidence={isLowConfidence}
              disabled={disabled}
              onCommit={(newText) => onSentenceChange(sIdx, newText)}
            />
            {isLowConfidence && !disabled && (
              <Tooltip title="Confirm this speaker label is correct">
                <IconButton
                  aria-label="Verify speaker label"
                  size="small"
                  onClick={() => onVerifySpeaker(sIdx)}
                  sx={{
                    // Always visible (not hover-gated): it flags a label that
                    // needs attention, so keyboard and touch users must see it.
                    p: 0.25,
                    ml: 0.25,
                    verticalAlign: "middle",
                    color: "warning.main",
                    "&:hover": { color: "success.main" },
                  }}
                >
                  <CheckCircleOutlineIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </Tooltip>
            )}
            {canSplit && (
              <Tooltip title="Reassign this sentence to another speaker">
                <IconButton
                  className="sentence-action"
                  size="small"
                  onClick={(e) =>
                    setSplitMenu({ anchor: e.currentTarget, sentenceIdx: sIdx })
                  }
                  sx={{
                    opacity: 0,
                    transition: "opacity 0.15s ease",
                    p: 0.25,
                    ml: 0.25,
                    verticalAlign: "middle",
                  }}
                >
                  <RecordVoiceOverIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </Tooltip>
            )}
            {canDeleteSentence && (
              <Tooltip title="Delete this sentence">
                <IconButton
                  className="sentence-action"
                  size="small"
                  onClick={() => onDeleteSentence(sIdx)}
                  sx={{
                    opacity: 0,
                    transition: "opacity 0.15s ease",
                    p: 0.25,
                    ml: 0.25,
                    verticalAlign: "middle",
                    color: "text.secondary",
                    "&:hover": { color: "error.main" },
                  }}
                >
                  <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          );
        })}
      </Typography>

      <Menu
        anchorEl={splitMenu?.anchor ?? null}
        open={Boolean(splitMenu)}
        onClose={() => setSplitMenu(null)}
      >
        <Typography
          variant="caption"
          sx={{ px: 2, py: 0.5, display: "block", color: "text.secondary" }}
        >
          Move sentence to…
        </Typography>
        {otherSpeakers.map((label) => (
          <MenuItem
            key={label}
            onClick={() => {
              if (splitMenu) onSplitSentence(splitMenu.sentenceIdx, label);
              setSplitMenu(null);
            }}
          >
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                mr: 1,
                backgroundColor: getSpeakerColorByLabel(
                  label,
                  speakers,
                  theme.palette.mode,
                ).primary,
              }}
            />
            {formatSpeakerLabel(label)}
          </MenuItem>
        ))}
      </Menu>
    </Paper>
  );
};
